/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useEffect, useRef, useState } from "react";
import { Box, Card, CircularProgress, Divider } from "@mui/material";
import { SyncLoader } from 'react-spinners';
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";

import { point, distance } from "@turf/turf";
import { useSoftUIController } from "hooks/context";
import DeviceSelectionCard from "components/Cards/DeviceSelectionCard";
import SoftButton from "components/SoftComponents/SoftButton";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const distanceLimitFilter = (device) => {
    const max_distance = Infinity;

    return device.value.distance_away < max_distance;
}

const filterOptions = (option, searchText) => {
    const { value } = option;
    const search = searchText.toLowerCase();

    return (
        value.name != "" && value.name.toLowerCase().includes(search) ||
        value.serial_number != "" && value.serial_number.toLowerCase().includes(search) ||
        value.stop_id != "" && value.stop_id.toLowerCase().includes(search) ||
        value.uuid != "" && value.uuid.toLowerCase().includes(search)
    )
}

function DeviceSelection({ formData }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { activeStep, isLastStep, user, selected_device, all_devices, deviceOptions }
    } = formData;

    const { location, organisation_id, devicePrimary } = formField;

    const [currentLocation, setCurrentLocation] = useState(values.location || null);
    const [searchDevice, setSearchDevice] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                // console.log(position.coords)
                setCurrentLocation([latitude, longitude]);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error detecting location:", error);
                // Check if the error was specifically a timeout

                if (error.code === error.TIMEOUT) {
                    console.warn("Location request timed out. Setting to 0,0.");
                }
                setCurrentLocation([0, 0]);
                setIsLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000, // 15 seconds in milliseconds
                maximumAge: 0   // Force fresh location
            }
        );
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const origin = point(currentLocation);
            const options = {
                units: "metres"
            };

            if (selected_device) {
                setSelectedDevice({
                    ...selected_device,
                    stop_id: selected_device.stop?.stop_id ? selected_device.stop.stop_id : selected_device.stop.site_id,
                    distance_away: (!currentLocation[0] && !currentLocation[1]) ? null : distance(origin, point([selected_device.position[1], selected_device.position[0]]), { units: "metres" })
                });
            }

            function locationSort(a, b) {
                if (currentLocation[0] == 0 && currentLocation[1] == 0) {
                    return a.serial_number.localeCompare(b.serial_number, 'en', { numeric: true });
                }
                else {
                    const pointA = point([a.position[1], a.position[0]]);
                    const pointB = point([b.position[1], b.position[0]]);

                    const distanceA = distance(origin, pointA, options);
                    const distanceB = distance(origin, pointB, options);

                    return distanceA - distanceB;
                }
            }

            const device_options = all_devices.sort(locationSort).map((device) => {
                // console.log([device.position[1], device.position[0]])
                // console.log(distance(origin, point([Number(device.position[1]), Number(device.position[0])]), options))

                return {
                    metadata: device,
                    value: {
                        name: device.name,
                        serial_number: device.serial_number,
                        stop_id: device.stop?.stop_id ? device.stop.stop_id : device.stop.site_id,
                        uuid: device.uuid,
                        distance_away: (!currentLocation[0] && !currentLocation[1]) ? null : distance(origin, point([Number(device.position[1]), Number(device.position[0])]), options)
                    },
                    label: device.name
                }
            });

            setDeviceOptions(device_options);
            // console.log(values, deviceOptions)
        }

    }, [isLoading]);

    const handleClick = (selected, e = false) => {
        if (e) {
            e.stopPropagation();
        }

        setFieldValue(devicePrimary.info.name, selected ? selected : "");
        setFieldValue(devicePrimary.uuid.name, selected ? selected.value.uuid : "");
        setFieldValue(devicePrimary.stop_id.name, selected ? selected.value.stop_id : "");
        setFieldValue(devicePrimary.serial_no.name, selected ? selected.value.serial_number : "");

        setFieldValue(organisation_id.name, selected ? selected.metadata.organisation_id : "");
        setFieldValue(location.name, selected ? [selected.metadata.position[1], selected.metadata.position[0]] : "");

        setFieldValue(devicePrimary.actions.installed.name, false);
    }

    // console.log(values)

    function calcDistanceSuffix(device_distance) {

        var method = "m";
        var distance_away = device_distance;

        if (distance_away > 100) {
            distance_away /= 1000;
            method = "km";
        };

        return (!currentLocation[0] && !currentLocation[1]) ? null : ({ type: "~", amount: `${distance_away.toFixed(2)}`, method });
    }

    return (
        <>
            {(selectedDevice && !isLoading) ? (
                <SoftBox>
                    <DeviceSelectionCard
                        title={selectedDevice.name}
                        description={[`Stop ID: ${selectedDevice.stop_id}`, `Serial No.: ${selectedDevice.serial_number}`]}
                        action={{ func: isSubmitting, onClick: (e) => handleClick({ value: selectedDevice, metadata: selectedDevice, label: selectedDevice.name }, e), type: "submit", label: "select" }}
                        value={calcDistanceSuffix(selectedDevice.distance_away)}
                    />
                </SoftBox>
            ) : (
                <SoftBox>
                    {!searchDevice ? (
                        <>
                            <SoftBox mb={3} display="flex" flexDirection="column" alignItems="center">
                                <SoftTypography variant="h4" fontWeight="bold">
                                    Device Selection
                                </SoftTypography>
                                <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                                    Determined based on your location. If these devices are not correct, use the <b>Manual Search</b> below.
                                </SoftTypography>
                            </SoftBox>

                            {isLoading ? (
                                <SoftBox
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight="200px"
                                >
                                    <CircularProgress color="info" />
                                    <SoftTypography variant="button" fontWeight="medium" mt={2} color="text">
                                        Locating nearby devices...
                                    </SoftTypography>
                                </SoftBox>
                            ) : (
                                <SoftBox>
                                    <SoftBox>
                                        <SoftBox mb={0.5}>
                                            <Grid container spacing={2}>
                                                {deviceOptions.filter(distanceLimitFilter).slice(0, 3).map((device_opt, index) => {
                                                    return (
                                                        <Grid item xxs={12} lg={4} key={index}>
                                                            <DeviceSelectionCard
                                                                title={device_opt.value.name}
                                                                description={[`Stop ID: ${device_opt.value.stop_id}`, `Serial No.: ${device_opt.value.serial_number}`]}
                                                                action={{ func: isSubmitting, onClick: (e) => handleClick(device_opt, e), type: "submit", label: "select" }}
                                                                value={calcDistanceSuffix(device_opt.value.distance_away)}
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </SoftBox>
                                        <SoftBox py={1}>
                                            <Divider />
                                        </SoftBox>
                                        <SoftBox>
                                            <SoftBox pb={1} display="flex" flexDirection="column" alignItems="center">
                                                <SoftButton color="dark" variant="gradient" sx={{ height: '100% !important', width: '100% !important', maxWidth: '400px' }} onClick={(e) => { setSearchDevice(true) }}>
                                                    Can't find your device?
                                                </SoftButton>
                                            </SoftBox>
                                        </SoftBox>
                                    </SoftBox>
                                </SoftBox>
                            )}
                        </>
                    ) : (
                        <>
                            <SoftBox pb={{ xxs: 4, lg: 4 }}>
                                <SoftButton color="dark" size="small" variant="outlined" onClick={(e) => { setSearchDevice(false) }}>
                                    <ArrowBackIosNewIcon size="large" sx={{ paddingRight: '8px', paddingBottom: '1px' }} />
                                    Return
                                </SoftButton>
                            </SoftBox>
                            <Grid container spacing={3}>
                                <Grid item xxs={12} lg={7}>
                                    <SoftBox display="flex" flexDirection="column" alignItems="center">
                                        <SoftTypography variant="h4" fontWeight="bold">
                                            Manual Search
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox mb={1} display="flex" flexDirection="column" alignItems="center">
                                        <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                                            Type to search by: <b>Device Name/Stop Name</b>, <b>Stop ID</b>, <b>Serial Number</b>, or <b>Site ID</b>
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox mb={2} sx={({ palette: { white }, borders: { borderRadius } }) => ({ maxWidth: '700px', borderRadius: borderRadius.md, backgroundColor: white.main })}>
                                        <SoftSelect
                                            name={devicePrimary.info.name}
                                            value={deviceOptions.find(opt => opt.label === values['devicePrimary-info']?.label) || ""}
                                            onChange={(selected) => handleClick(selected)}
                                            filterOption={filterOptions}
                                            options={deviceOptions}
                                            isClearable={true}
                                            isSearchable={true}
                                            placeholder="Search or select a device..."

                                            error={errors.devicePrimary?.info && touched.devicePrimary?.info}
                                        />
                                    </SoftBox>
                                </Grid>
                                <Grid item xxs={12} lg={5}>
                                    {typeof values['devicePrimary-info'] != 'string' && (
                                        <DeviceSelectionCard
                                            title={values['devicePrimary-info']?.label}
                                            description={[`Stop ID: ${values['devicePrimary-stop_id']}`, `Serial No.: ${values['devicePrimary-serial_no']}`]}
                                            action={{ func: isSubmitting, type: "submit", label: "select" }}
                                            value={calcDistanceSuffix(values['devicePrimary-info']?.value.distance_away)}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <SoftBox mt={searchDevice ? 5 : 1} width="100%" display="flex" justifyContent="space-between">
                        <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                            {activeStep === 0 ? "cancel" : "back"}
                        </SoftButton>
                    </SoftBox>
                </SoftBox>
            )}
        </>
    );
}

// typechecking props for DeviceSelection
DeviceSelection.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired
};

export default DeviceSelection;
